/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
/**
 * SignpostChart.js
 *
 * Recharts time-series chart for quantitative signpost data points.
 * - Single-region: one line + threshold reference lines + breach/trend badges.
 * - Multi-region: one coloured line per region, legend shows region names.
 *   Thresholds and breach detection still apply (evaluated against each region's latest value).
 */
import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Chip, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { protectedResources } from "authConfig";

const SCENARIO_COLORS = {
  pessimistic: "#d32f2f",
  base: "#f57c00",
  optimistic: "#1976d2",
};

const REGION_COLORS = [
  "#52009b",
  "#e91e63",
  "#2196f3",
  "#ff9800",
  "#4caf50",
  "#00bcd4",
  "#9c27b0",
  "#f44336",
];

function thresholdColor(scenario) {
  return SCENARIO_COLORS[scenario] || "#888";
}

function thresholdLabel(t) {
  const dir = t.direction === "above" ? "↑ above" : "↓ below";
  return `${t.scenarioLabel || t.scenario} ${dir} ${t.value}`;
}

const PROXIMITY_COLORS = {
  triggered: "#d32f2f",
  imminent: "#f57c00",
  approaching: "#fbc02d",
  far: "#388e3c",
};
const PROXIMITY_LABELS = {
  triggered: "Threshold triggered",
  imminent: "Imminent breach",
  approaching: "Approaching threshold",
  far: "Far from threshold",
};
const SCENARIO_LABELS = {
  pessimistic: "Pessimistic scenario",
  optimistic: "Optimistic scenario",
  base: "Base scenario",
};

export default function SignpostChart({
  signpostId,
  thresholds = [],
  unit = "",
  execute,
  lastAssessment,
}) {
  const [rawPoints, setRawPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!signpostId || !execute) return;
    setLoading(true);
    execute(
      "GET",
      `${protectedResources.apiTodoList.endpoint}/emergingRisk/signposts/${signpostId}/datapoints`
    )
      .then((res) => {
        const pts = res?.data ?? [];
        setRawPoints(Array.isArray(pts) ? pts : []);
      })
      .catch(() => setRawPoints([]))
      .finally(() => setLoading(false));
  }, [signpostId]);

  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;

  if (rawPoints.length === 0) {
    return (
      <MDTypography variant="caption" color="text.secondary">
        No data points yet. The poller will populate this chart on its next run.
      </MDTypography>
    );
  }

  // ── Detect whether data is multi-region ──────────────────────────────────
  const regionNames = [...new Set(rawPoints.map((p) => p.region).filter(Boolean))];
  const isMultiRegion = regionNames.length > 0;

  if (isMultiRegion) {
    // Build a date-keyed map: { date → { RegionA: val, RegionB: val, ... } }
    const dateMap = {};
    rawPoints.forEach((p) => {
      const d = p.date ? new Date(p.date).toLocaleDateString() : "";
      if (!dateMap[d]) dateMap[d] = { date: d };
      if (p.region) {
        const num = typeof p.value === "number" ? p.value : parseFloat(p.value);
        dateMap[d][p.region] = Number.isNaN(num) ? null : num;
      }
    });
    const chartData = Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));

    // Breach detection per region (latest value per region vs thresholds)
    const latestByRegion = {};
    rawPoints.forEach((p) => {
      if (!p.region) return;
      const existing = latestByRegion[p.region];
      if (!existing || new Date(p.date) > new Date(existing.date)) {
        latestByRegion[p.region] = p;
      }
    });
    const breachedRegions = [];
    regionNames.forEach((rName) => {
      const latest = latestByRegion[rName];
      if (!latest) return;
      const val = typeof latest.value === "number" ? latest.value : parseFloat(latest.value);
      if (Number.isNaN(val)) return;
      thresholds.forEach((t) => {
        const breached = t.direction === "above" ? val > t.value : val < t.value;
        if (breached) breachedRegions.push({ region: rName, threshold: t, value: val });
      });
    });

    return (
      <MDBox>
        {breachedRegions.length > 0 && (
          <MDBox mb={1} display="flex" gap={0.5} flexWrap="wrap">
            {breachedRegions.map((b, i) => (
              <Chip
                key={i}
                size="small"
                label={`${b.region}: ${thresholdLabel(b.threshold)}`}
                sx={{
                  bgcolor: thresholdColor(b.threshold.scenario),
                  color: "#fff",
                  fontSize: "11px",
                }}
              />
            ))}
          </MDBox>
        )}
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              label={
                unit
                  ? { value: unit, angle: -90, position: "insideLeft", style: { fontSize: 11 } }
                  : undefined
              }
            />
            <RechartsTooltip
              formatter={(val, name) => [
                val !== null ? `${val}${unit ? ` ${unit}` : ""}` : "—",
                name,
              ]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {regionNames.map((rName, idx) => (
              <Line
                key={rName}
                type="monotone"
                dataKey={rName}
                stroke={REGION_COLORS[idx % REGION_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            ))}
            {thresholds.map((t, i) => (
              <ReferenceLine
                key={i}
                y={t.value}
                stroke={thresholdColor(t.scenario)}
                strokeDasharray="6 3"
                strokeWidth={1.5}
                label={{
                  value: thresholdLabel(t),
                  fill: thresholdColor(t.scenario),
                  fontSize: 10,
                  position: t.direction === "above" ? "insideTopRight" : "insideBottomRight",
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </MDBox>
    );
  }

  // ── Single-region (original behaviour) ──────────────────────────────────
  const data = rawPoints.map((p) => ({
    date: p.date ? new Date(p.date).toLocaleDateString() : "",
    value: typeof p.value === "number" ? p.value : parseFloat(p.value) || 0,
  }));

  const latestValue = data.length > 0 ? data[data.length - 1].value : null;
  const breached = thresholds.filter((t) => {
    if (latestValue === null) return false;
    return t.direction === "above" ? latestValue > t.value : latestValue < t.value;
  });

  const proximity = lastAssessment?.direction;
  const trendScenario = lastAssessment?.trendScenario;
  // Prefer the specific scenario name (e.g. "Middle Iran") over the pole label.
  const trendScenarioName =
    lastAssessment?.trendScenarioLabel || SCENARIO_LABELS[trendScenario] || trendScenario;
  const narrative = lastAssessment?.narrative;
  const assessedAt = lastAssessment?.assessedAt
    ? new Date(lastAssessment.assessedAt).toLocaleString()
    : null;
  // Deterministic scenario-probability forecast (GBM barrier-crossing), when the poller had enough
  // history to fit it. Show the longest horizon's P(scenario) as coloured chips.
  const forecast = lastAssessment?.forecast;
  const fcHorizon = forecast?.horizons?.length
    ? forecast.horizons[forecast.horizons.length - 1]
    : null;
  const fcDays = fcHorizon ? Math.round((fcHorizon / 21) * 30) : null;
  const fcScenarios = (forecast?.scenarios || [])
    .map((s) => ({ ...s, p: (s.prob && s.prob[fcHorizon]) || 0 }))
    .filter((s) => s.p >= 0.01)
    .sort((a, b) => b.p - a.p);

  return (
    <MDBox>
      {breached.length > 0 && (
        <MDBox mb={1} display="flex" gap={0.5} flexWrap="wrap">
          {breached.map((t, i) => (
            <Chip
              key={i}
              size="small"
              label={`Breach: ${thresholdLabel(t)}`}
              sx={{ bgcolor: thresholdColor(t.scenario), color: "#fff", fontSize: "11px" }}
            />
          ))}
        </MDBox>
      )}
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 11 }}
            label={
              unit
                ? { value: unit, angle: -90, position: "insideLeft", style: { fontSize: 11 } }
                : undefined
            }
          />
          <RechartsTooltip formatter={(val) => [`${val}${unit ? ` ${unit}` : ""}`, "Value"]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#52009b"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            name="Observed value"
          />
          {thresholds.map((t, i) => (
            <ReferenceLine
              key={i}
              y={t.value}
              stroke={thresholdColor(t.scenario)}
              strokeDasharray="6 3"
              strokeWidth={1.5}
              label={{
                value: thresholdLabel(t),
                fill: thresholdColor(t.scenario),
                fontSize: 10,
                position: t.direction === "above" ? "insideTopRight" : "insideBottomRight",
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      {(proximity || narrative) && (
        <MDBox
          mt={1.5}
          p={1.5}
          sx={{ bgcolor: "#f9f6ff", borderRadius: 1, borderLeft: "3px solid #52009b" }}
        >
          <MDTypography variant="subtitle2" sx={{ color: "#52009b", mb: 0.5 }}>
            Intelligence
          </MDTypography>
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={narrative ? 0.5 : 0}>
            {trendScenario && (
              <Chip
                size="small"
                label={`Trending towards: ${trendScenarioName}`}
                sx={{
                  bgcolor:
                    trendScenario === "pessimistic"
                      ? "#d32f2f"
                      : trendScenario === "optimistic"
                      ? "#388e3c"
                      : "#f57c00",
                  color: "#fff",
                  fontSize: "11px",
                }}
              />
            )}
            {proximity && (
              <Chip
                size="small"
                label={PROXIMITY_LABELS[proximity] || proximity}
                sx={{
                  bgcolor: PROXIMITY_COLORS[proximity] || "#888",
                  color: "#fff",
                  fontSize: "11px",
                }}
              />
            )}
          </MDBox>
          {fcScenarios.length > 0 && (
            <MDBox display="flex" gap={0.75} flexWrap="wrap" alignItems="center" mb={0.5}>
              <MDTypography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                {`~${fcDays}d forecast:`}
              </MDTypography>
              {fcScenarios.map((s, i) => (
                <Chip
                  key={i}
                  size="small"
                  label={`${Math.round(s.p * 100)}% ${s.label}`}
                  sx={{
                    bgcolor:
                      s.scenario === "pessimistic"
                        ? "#d32f2f"
                        : s.scenario === "optimistic"
                        ? "#388e3c"
                        : "#f57c00",
                    color: "#fff",
                    fontSize: "11px",
                  }}
                />
              ))}
            </MDBox>
          )}
          {narrative && (
            <MDTypography
              variant="caption"
              sx={{ color: "#444", fontStyle: "italic", display: "block" }}
            >
              {narrative}
            </MDTypography>
          )}
          {assessedAt && (
            <MDTypography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Last assessed: {assessedAt}
            </MDTypography>
          )}
        </MDBox>
      )}
    </MDBox>
  );
}
